import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import type { IAppsSignalFilters } from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import type { SignalFilters, SignalSource, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalSourceMap, signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { GroupedTechnologies } from "app/pages/sales-intelligence/domain/technology/types/model";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import IntentSignalFilterView
    from "app/pages/sales-intelligence/sub-modules/signals/components/views/IntentSignalFilterView/IntentSignalFilterView";
import RoleChangesSignalFiltersView
    from "app/pages/sales-intelligence/sub-modules/signals/components/views/RoleChangesSignalFiltersView/RoleChangesSignalFiltersView";
import withSignalValidation from "app/pages/sales-intelligence/sub-modules/signals/hoc/withSignalValidation";
import type IntentSignalFilters
    from "app/pages/sales-intelligence/sub-modules/signals/models/intent/IntentSignalFilters";
import type IRoleChangesSignalFilters
    from "app/pages/sales-intelligence/sub-modules/signals/models/role-changes/RoleChangesSignalFilters";
import type SignalFiltersView from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersView";
import type SignalFiltersViewProps
    from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import type SignalFiltersViewResolver
    from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewResolver";
import type { ICountryObject } from "app/services/CountryService";

import AdNetworkSignalFiltersView from "../../components/views/AdNetworkSignalFiltersView/AdNetworkSignalFiltersView";
import AppsSignalFiltersView from "../../components/views/AppsSignalFiltersView/AppsSignalFiltersView";
import EcommerceSignalFiltersView from "../../components/views/EcommerceSignalFiltersView/EcommerceSignalFiltersView";
import AnyNewsSignalFiltersView from "../../components/views/NewsSignalFiltersView/AnyNewsSignalFiltersView";
import TechnologySignalFiltersView
    from "../../components/views/TechnologySignalFiltersView/TechnologySignalFiltersView";
import TrafficSignalFiltersView from "../../components/views/TrafficSignalFiltersView/TrafficSignalFiltersView";
import type AdNetworkSignalFilters from "../../models/ad-network/AdNetworkSignalFilters";
import type EcommerceSignalFilters from "../../models/ecommerce/EcommerceSignalFilters";
import type { SignalsDescription } from "../../models/miscellaneous/SignalsDescription";
import type { NewsCategoriesByType } from "../../models/news/NewsCategoriesByType";
import type TechnologySignalFilters from "../../models/technology/TechnologySignalFilters";
import type TrafficSignalFilters from "../../models/traffic/TrafficSignalFilters";

type FactoryDeps = Pick<SignalsDescription, "adNetworks"> & {
    countries: readonly ICountryObject[];
    technologies: GroupedTechnologies;
    newsCategories: NewsCategoriesByType;
    intentSelectedTopics: readonly Topic[];
    intentUserCountries: readonly ICountryObject[];
    signalSource: SignalSource;
    sdkDescriptions: readonly IAppsSdkDescriptionItemDto[];
    isSdkDescriptionsPending: boolean;
};

export default function createSignalFiltersViewResolver(deps: FactoryDeps): SignalFiltersViewResolver {
    const {
        adNetworks,
        countries,
        technologies,
        intentUserCountries,
        intentSelectedTopics,
        signalSource,
        sdkDescriptions,
        isSdkDescriptionsPending,
    } = deps;

    const IntentSignalFiltersViewWrapper = <Filters extends SignalFilters>(props: SignalFiltersViewProps<Filters>) => {
        return (
            <IntentSignalFilterView
                {...(props as SignalFiltersViewProps<IntentSignalFilters>)}
                topics={intentSelectedTopics}
                countries={intentUserCountries}
            />
        );
    };

    const AdNetworkSignalFiltersViewWrapper = <Filters extends SignalFilters>(
        props: SignalFiltersViewProps<Filters>,
    ) => {
        return (
            <AdNetworkSignalFiltersView
                adNetworks={adNetworks}
                {...(props as unknown as SignalFiltersViewProps<AdNetworkSignalFilters>)}
            />
        );
    };

    const TechnologySignalFiltersViewWrapper = <Filters extends SignalFilters>(
        props: SignalFiltersViewProps<Filters>,
    ) => {
        return (
            <TechnologySignalFiltersView
                technologies={technologies}
                {...(props as unknown as SignalFiltersViewProps<TechnologySignalFilters>)}
            />
        );
    };

    const AppsSignalFiltersViewWrapper = <Filters extends SignalFilters>(props: SignalFiltersViewProps<Filters>) => {
        return (
            <AppsSignalFiltersView
                sdkDescriptions={sdkDescriptions}
                isSdkDescriptionsPending={isSdkDescriptionsPending}
                {...(props as unknown as SignalFiltersViewProps<Partial<IAppsSignalFilters>>)}
            />
        );
    };

    const AnyNewsSignalFiltersViewWrapper = <Filters extends SignalFilters>(props: SignalFiltersViewProps<Filters>) => {
        return (
            <AnyNewsSignalFiltersView
                {...props}
                newsCategories={deps.newsCategories}
            />
        );
    };

    const TrafficSignalFiltersViewWrapper = <Filters extends SignalFilters>(props: SignalFiltersViewProps<Filters>) => {
        return (
            <TrafficSignalFiltersView
                countries={countries}
                {...(props as unknown as SignalFiltersViewProps<TrafficSignalFilters>)}
            />
        );
    };

    const EcommerceSignalFiltersViewWrapper = <Filters extends SignalFilters>(
        props: SignalFiltersViewProps<Filters>,
    ) => {
        return <EcommerceSignalFiltersView {...(props as unknown as SignalFiltersViewProps<EcommerceSignalFilters>)} />;
    };

    const isSharedSignalContext = signalSource === signalSourceMap.account;

    const RoleChangesSignalFiltersViewWrapper = <Filters extends SignalFilters>(
        props: SignalFiltersViewProps<Filters>,
    ) => {
        return (
            <RoleChangesSignalFiltersView
                {...(props as unknown as SignalFiltersViewProps<IRoleChangesSignalFilters>)}
                isShared={isSharedSignalContext}
            />
        );
    };

    function getFiltersView(signalType: SignalType): SignalFiltersView<SignalFilters> | undefined {
        switch (signalType) {
            case signalTypeMap.adNetwork:
                return AdNetworkSignalFiltersViewWrapper;
            case signalTypeMap.technology:
                return TechnologySignalFiltersViewWrapper;
            case signalTypeMap.traffic:
                return TrafficSignalFiltersViewWrapper;
            case signalTypeMap.ecommerce:
                return EcommerceSignalFiltersViewWrapper;
            case signalTypeMap.news:
                return AnyNewsSignalFiltersViewWrapper;
            case signalTypeMap.intent:
                return IntentSignalFiltersViewWrapper;
            case signalTypeMap.apps:
                return AppsSignalFiltersViewWrapper;
            case signalTypeMap.roleChanges:
                return RoleChangesSignalFiltersViewWrapper;
            default:
                return undefined;
        }
    }

    function getFiltersViewWithValidation(signalType: SignalType): SignalFiltersView<SignalFilters> | undefined {
        const View = getFiltersView(signalType);

        if (typeof View === "undefined") {
            return undefined;
        }

        return withSignalValidation(View, signalType, signalSource);
    }

    return { getFiltersView, getFiltersViewWithValidation };
}
